import React, {
  useState,
  useRef,
  useEffect,
  MouseEvent,
  useLayoutEffect,
} from "react";
import styles from "./dropdown.module.scss";
import CheckboxList from "../checkbox-list/checkbox-list";
import closeIcon from "../../../images/clear-16.svg";
import CustomScrollbar from "../scrollbar/scrollbar";

interface DropdownClasses {
  label?: string;
  dropdown?: string;
}

interface DropdownProps<T> {
  title?: string;
  options: Map<T, string>;
  placeholder?: string;
  className?: string;
  onChange?: (selectedOption: T | T[] | null) => void;
  value?: T | T[];
  disabled?: boolean;
  listType?: "single" | "checkbox";
  onClear?: () => void;
  customDisplay?: (value: string) => string;
  classes?: DropdownClasses;
  mpmpValue?: string;
  canUnset?: boolean;
}

function hasValue<V>(val: V): val is NonNullable<V> {
  if (val === undefined || val === null) return false;
  if (typeof val === "string" && val.trim() === "") return false;
  return !(Array.isArray(val) && val.length === 0);
}

const Dropdown = <T extends number | string>({
  title,
  options,
  placeholder = "",
  className = "",
  onChange,
  value,
  disabled,
  listType = "single",
  onClear,
  customDisplay,
  classes = {},
  mpmpValue,
  canUnset = true,
}: DropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<T | undefined>(
    listType === "single" && hasValue(value) && !Array.isArray(value)
      ? (value as T)
      : undefined,
  );
  const [selectedOptions, setSelectedOptions] = useState<T[]>(
    Array.isArray(value) ? value : [],
  );
  const ref = useRef<HTMLDivElement | null>(null);
  const dropdownContentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (isOpen && dropdownContentRef.current) {
      const height = dropdownContentRef.current.scrollHeight;
      setContentHeight(height);
    }
  }, [isOpen, options]);

  useEffect(() => {
    if (listType === "single") {
      setSelectedOption(hasValue(value) ? (value as T) : undefined);
    } else if (listType === "checkbox") {
      setSelectedOptions(Array.isArray(value) ? value : []);
    }
  }, [value, listType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | Event) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelectSingle = (option: T) => {
    if (selectedOption === option && canUnset) {
      setSelectedOption(undefined);
      if (onChange) {
        onChange(null);
      }
    } else {
      setSelectedOption(option);
      if (onChange) {
        onChange(option);
      }
    }
    setIsOpen(false);
  };

  const handleSelectMultiple = (option: T) => {
    let updatedSelectedOptions: T[];
    if (selectedOptions.includes(option)) {
      updatedSelectedOptions = selectedOptions.filter(
        (item) => item !== option,
      );
    } else {
      updatedSelectedOptions = [...selectedOptions, option];
    }
    setSelectedOptions(updatedSelectedOptions);
    if (onChange) {
      onChange(updatedSelectedOptions);
    }
  };

  const renderDropdownContent = () => {
    if (listType === "checkbox") {
      const checkboxItems = Array.from(options, ([key, option]) => ({
        key: key,
        label: option,
      }));
      return (
        <div ref={dropdownContentRef}>
          <CheckboxList
            items={checkboxItems}
            selectedValues={selectedOptions}
            onItemChange={handleSelectMultiple}
            classes={{
              label: styles.checkboxLabel,
              checkboxList: styles.checkboxList,
              checkbox: styles.checkbox,
            }}
            size="medium"
          />
        </div>
      );
    } else {
      return (
        <div ref={dropdownContentRef}>
          {Array.from(options, ([key, option]) =>
            key === selectedOption ? null : (
              <div
                key={key}
                onClick={() => handleSelectSingle(key)}
                className={`${styles.dropdownItem} ${key === selectedOption && canUnset ? styles.selected : ""}`}
              >
                {option}
              </div>
            ),
          )}
        </div>
      );
    }
  };

  const displayValue = () => {
    if (mpmpValue) return mpmpValue;
    if (mpmpValue === "")
      return <span className={styles.placeholder}>{placeholder}</span>;

    if (listType === "checkbox") {
      if (selectedOptions.length > 0) {
        return (
          <div className={styles.overflow}>
            {options instanceof Map &&
              selectedOptions
                .filter((value) => options.has(value))
                .map((value) =>
                  customDisplay
                    ? customDisplay(options.get(value)!)
                    : options.get(value),
                )
                .join(", ")}
          </div>
        );
      }
      return <span className={styles.placeholder}>{placeholder}</span>;
    }

    if (!hasValue(selectedOption) || !options.has(selectedOption)) {
      return <span className={styles.placeholder}>{placeholder}</span>;
    }

    return customDisplay
      ? customDisplay(options.get(selectedOption)!)
      : options.get(selectedOption)!;
  };

  return (
    <div
      className={`${styles.container} ${disabled ? styles.disabled : ""}`}
      ref={ref}
    >
      {title && (
        <div className={`${styles.label} ${classes.label || ""}`}>{title}</div>
      )}
      <div
        className={`${styles.dropdown} ${className} ${classes.dropdown || ""} ${isOpen ? styles.open : ""} ${!isOpen ? styles.hoverable : ""} ${(listType === "checkbox" ? selectedOptions.length > 0 : hasValue(selectedOption)) ? styles.filled : ""}`}
        onClick={toggleDropdown}
      >
        {displayValue()}
        <span className={styles.arrow}>
          {onClear &&
          !isOpen &&
          ((Array.isArray(value) ? value.length > 0 : hasValue(value)) ||
            !!mpmpValue) ? (
            <img
              src={closeIcon}
              alt="clear"
              className={styles.clearIcon}
              onClick={(event) => {
                event.stopPropagation();
                onClear();
                setSelectedOption(undefined);
              }}
            />
          ) : (
            <div
              className={styles.icon}
              style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          )}
        </span>
      </div>
      {isOpen && (
        <div className={styles.dropdownList}>
          <CustomScrollbar
            minHeight={
              contentHeight && contentHeight < 200
                ? `${contentHeight}px`
                : "200px"
            }
          >
            {renderDropdownContent()}
          </CustomScrollbar>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
